'use client';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  DateInput,
  ExpandableCallout,
  FieldError,
  SingleSelect,
  type WebDateInputProps,
} from '@galapagos/design-system/web';
import {
  MEMBER_GENDER_IDENTITY_OPTIONS,
  MEMBER_PRONOUN_OPTIONS,
  SEX_ASSIGNED_AT_BIRTH,
  sexAssignedAtBirthSchema,
  type SexAssignedAtBirth,
} from '@galapagos/shared';
import { FormActions } from '../components/FormActions';
import { GenderInfoFields } from '../components/GenderInfoFields';
import { PageTitle } from '../components/PageTitle';
import { PrivacyNote } from '../components/PrivacyNote';
import { PrivateFields } from '../components/PrivateFields';
import { SignupShell } from '../components/SignupShell';
import { ensureSignupStepStyle } from '../components/signup-step-style';
import { useLeaveOnSponsorFailure } from '../hooks/useLeaveOnSponsorFailure';
import { useActiveSponsorQuery } from '../hooks/useActiveSignupSponsor';
import { useSignupApi } from '../hooks/useSignupApi';
import { useSignupNavigation } from '../hooks/useSignupNavigation';
import { useSignupSponsor } from '../hooks/useSignupSponsor';
import { useStepHeading } from '../hooks/useStepHeading';
import { startQuietEligibilityCheck } from '../profile/eligibility-poller';
import { selectionFromStored, storedFromSelection } from '../profile/gender-info';
import {
  buildEligibilityRequest,
  validateBirthDate,
  type BirthDateErrors,
} from '../profile/profile-answers';
import { useSignupLegalName } from '../profile/signup-identity';
import { startSsoEligibilityCheck } from '../profile/sso-eligibility';
import { linkNamesSponsor } from '../services/sponsor-lookup';
import { useSignupFlowStore } from '../stores/signup-flow-store';
import { EarlierAnswersGate } from './EarlierAnswersGate';

type DateInputParts = NonNullable<WebDateInputProps['value']>;

const SEX_LABEL_KEYS = {
  male: 'birthDate.sexMale',
  female: 'birthDate.sexFemale',
} as const satisfies Record<SexAssignedAtBirth, string>;

const EMPTY_PARTS: DateInputParts = { month: '', day: '', year: '' };

function BirthDateStep() {
  const { t } = useTranslation('signup');
  const api = useSignupApi();
  // The path's sponsor: on an employer link, the one it names; on a search
  // path, the pick (none yet at this step, since the search comes later).
  const { clientId, subClientId } = useActiveSponsorQuery();
  const sponsor = useSignupSponsor(clientId, subClientId);
  useLeaveOnSponsorFailure(sponsor, clientId);
  const legalName = useSignupLegalName();
  const { goToNextStep, path } = useSignupNavigation();
  // An employer link's check always names the employer, so it waits for the
  // lookup rather than going in as if there were none. A search path never
  // waits: its sponsor is a pick that may be a decoy, whose lookup can fail.
  const awaitingSponsor =
    path === 'employer-link' && linkNamesSponsor(clientId) && sponsor.data === undefined;
  const setBirthDate = useSignupFlowStore((state) => state.setBirthDate);
  const profileAddress = useSignupFlowStore((state) => state.profileAddress);
  const storedSex = useSignupFlowStore((state) => state.sexAssignedAtBirth);
  const setSexAssignedAtBirth = useSignupFlowStore((state) => state.setSexAssignedAtBirth);
  const storedIdentity = useSignupFlowStore((state) => state.genderIdentity);
  const storedPronouns = useSignupFlowStore((state) => state.pronouns);
  const setGenderIdentity = useSignupFlowStore((state) => state.setGenderIdentity);
  const setPronouns = useSignupFlowStore((state) => state.setPronouns);
  const [parts, setParts] = useState<DateInputParts>(EMPTY_PARTS);
  const [isoDate, setIsoDate] = useState<string | null>(null);
  const [sex, setSex] = useState<SexAssignedAtBirth | null>(storedSex);
  const [errors, setErrors] = useState<BirthDateErrors>({});
  // Collapsed unless the member already gave one of these, as production does.
  const [genderInfoOpen, setGenderInfoOpen] = useState(
    storedIdentity !== null || storedPronouns !== null,
  );
  const [identity, setIdentity] = useState(() =>
    selectionFromStored(storedIdentity, MEMBER_GENDER_IDENTITY_OPTIONS),
  );
  const [pronouns, setPronounsSelection] = useState(() =>
    selectionFromStored(storedPronouns, MEMBER_PRONOUN_OPTIONS),
  );
  const title = t('birthDate.title');
  const headingRef = useStepHeading(title);
  ensureSignupStepStyle();

  const onContinue = () => {
    if (awaitingSponsor) return;
    const found = validateBirthDate(isoDate, sex);
    setErrors(found);
    if (found.date || found.sex || isoDate === null || sex === null) return;
    setSexAssignedAtBirth(sex);
    // Hiding the section keeps what was picked, as production does.
    setGenderIdentity(storedFromSelection(identity));
    setPronouns(storedFromSelection(pronouns));
    setBirthDate(isoDate);
    const regularCheck = () =>
      legalName
        ? buildEligibilityRequest({
            legalName,
            dateOfBirth: isoDate,
            zip: profileAddress?.zip,
            clientRef: sponsor.data?.clientRef,
          })
        : null;
    // An SSO member's check comes from the employer's roster while the SSO
    // session lasts; the regular check is the fallback once it is gone.
    const ssoHandled =
      path === 'sso' &&
      startSsoEligibilityCheck(api, { dateOfBirth: isoDate, fallback: regularCheck });
    // A link without an employer asks this before the account exists, so its
    // check waits until after sign-in (the eligibility-check step).
    if (!ssoHandled && path !== 'no-employer') {
      const request = regularCheck();
      if (request) startQuietEligibilityCheck(api, request);
    }
    goToNextStep('birth-date');
  };

  return (
    <SignupShell>
      <div className="signup-step">
        <PageTitle title={title} headingRef={headingRef} />
        <ExpandableCallout
          title={t('birthDate.infoTitle')}
          actionId="signup.birth-date-screen-why.toggle"
          data-testid="signup-birth-date-why"
        >
          {t('birthDate.infoBody')}
        </ExpandableCallout>
        {/* The one status announcer for this step: always mounted, so the
            sex-at-birth error is heard when it appears. The date input
            announces its own error. */}
        <p className="signup-visually-hidden" aria-live="polite" data-testid="signup-status">
          {errors.sex ? t(errors.sex) : ''}
        </p>
        <PrivateFields>
          <DateInput
            actionId="signup.birth-date-screen-date.change"
            legend={t('birthDate.legend')}
            partLabels={{
              month: t('birthDate.monthLabel'),
              day: t('birthDate.dayLabel'),
              year: t('birthDate.yearLabel'),
            }}
            helperText={t('birthDate.helper')}
            errorText={errors.date ? t(errors.date) : undefined}
            autoComplete="bday"
            name="birth-date"
            value={parts}
            onChangeValue={(value) => {
              setParts({ month: value.month, day: value.day, year: value.year });
              setIsoDate(value.isoDate);
              setErrors((current) => ({ ...current, date: undefined }));
            }}
            data-testid="signup-birth-date"
          />
          <SingleSelect
            actionId="signup.birth-date-screen-sex.change"
            label={t('birthDate.sexLabel')}
            options={SEX_ASSIGNED_AT_BIRTH.map((value) => ({
              value,
              label: t(SEX_LABEL_KEYS[value]),
            }))}
            value={sex}
            invalid={Boolean(errors.sex)}
            onSelect={(value) => {
              const picked = sexAssignedAtBirthSchema.safeParse(value);
              if (!picked.success) return;
              setSex(picked.data);
              setErrors((current) => ({ ...current, sex: undefined }));
            }}
            data-testid="signup-sex"
          />
          <FieldError
            live={false}
            message={errors.sex ? t(errors.sex) : undefined}
            data-testid="signup-sex-error"
          />
          <GenderInfoFields
            open={genderInfoOpen}
            onToggle={() => setGenderInfoOpen((current) => !current)}
            identity={identity}
            onIdentityChange={setIdentity}
            pronouns={pronouns}
            onPronounsChange={setPronounsSelection}
          />
        </PrivateFields>
        <PrivacyNote text={t('birthDate.privacyNote')} />
        <FormActions>
          <Button
            size="L"
            type="Primary"
            label={t('actions.continue')}
            state={awaitingSponsor ? 'Loading' : 'Default'}
            onPress={onContinue}
            actionId="signup.birth-date-screen-continue.activate"
            data-testid="signup-continue"
          />
        </FormActions>
      </div>
    </SignupShell>
  );
}

/** `/birth-date/`: date of birth and sex assigned at birth (v9 1.7). */
export function BirthDateStepScreen() {
  return (
    <EarlierAnswersGate step="birth-date">
      <BirthDateStep />
    </EarlierAnswersGate>
  );
}
